import React from "react";
import {
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Box,
  useTabsContext,
} from "@chakra-ui/react";

import FaqTab from "./tabs/FaqTab";
import InquiriesTab from "./tabs/InquiriesTab";
import ResourcesTab from "./tabs/ResourcesTab";
import { FaqsIcon } from "@root/assets/icons/FaqsIcon";
import { ResourcesIcon } from "@root/assets/icons/ResourcesIcon";
import { InquiriesIcon } from "@root/assets/icons/InquiriesIcon";
import { useTranslation } from "react-i18next";
import useMediaQ from "@root/hooks/useMediaQ";

const TabIconLabel = ({
  idx,
  label,
  iconFn,
}: {
  idx: number;
  label: string;
  iconFn: (color: string) => JSX.Element;
}) => {
  const { selectedIndex } = useTabsContext();
  const { sm } = useMediaQ();
  const isMobile = !sm;
  const isSelected = selectedIndex === idx;

  return (
    <Box
      display="flex"
      alignItems="center"
      gap={isMobile ? 1 : 2}
      fontWeight="bold"
    >
      {iconFn(isSelected ? (isMobile ? "white" : "#1819B4") : "#2F2F34")}
      {label}
    </Box>
  );
};

interface CustomerCenterTabsProps {
  tabIndex?: number;
  onTabChange?: (index: number) => void;
}

const CustomerCenterTabs: React.FC<CustomerCenterTabsProps> = ({
  tabIndex,
  onTabChange,
}) => {
  const { t } = useTranslation("common");
  const { sm } = useMediaQ();
  const isMobile = !sm;
  const isControlled = tabIndex !== undefined && onTabChange !== undefined;

  const tabData = [
    {
      id: "faq",
      label: t("customerCenter.faqsTab"),
      icon: (color: string) => <FaqsIcon color={color} />,
    },
    {
      id: "inquiries",
      label: t("customerCenter.inquiriesTab"),
      icon: (color: string) => <InquiriesIcon color={color} />,
    },
    {
      id: "resources",
      label: t("customerCenter.resourcesTab"),
      icon: (color: string) => <ResourcesIcon color={color} />,
    },
  ];
  return (
    <Box w="100%" maxW="full" overflowY={"auto"} mx="auto">
      <Tabs
        variant="unstyled"
        isFitted
        w="100%"
        index={isControlled ? tabIndex : undefined}
        onChange={isControlled ? onTabChange : undefined}
      >
        <TabList
          mb={4}
          w="100%"
          background={
            isMobile
              ? "transparent"
              : "linear-gradient(0deg, rgba(205, 214, 220, 0.32), rgba(205, 214, 220, 0.32))"
          }
          h={isMobile ? "auto" : "51px"}
          borderRadius={isMobile ? "0" : "10px"}
          p={isMobile ? 0 : 1}
          display="flex"
          boxShadow={isMobile ? "none" : "sm"}
          overflowX="auto"
          whiteSpace="nowrap"
          gap={isMobile ? "8px" : "0"}
        >
          {tabData?.map((tab, idx) => (
            <Tab
              key={tab.label}
              data-tour-item={
                tab.id === "faq"
                  ? "journalist-customer-center-faq"
                  : tab.id === "inquiries"
                    ? "journalist-customer-center-inquiries-tab"
                    : tab.id === "resources"
                      ? "journalist-customer-center-resources-tab"
                      : undefined
              }
              w="33.33%"
              fontWeight="600"
              fontSize={isMobile ? "14px" : "18px"}
              color="gray.700"
              _selected={{
                color: isMobile ? "white" : "#1819B4",
                bg: isMobile ? "#1819B4" : "white",
                border: isMobile ? "none" : "0.5px solid #A7B2BA",
                boxShadow: isMobile ? "none" : "0px 1px 4px 0px #0000001F",
                borderRadius: isMobile ? "100px" : "10px",
              }}
              _focus={{ boxShadow: "none" }}
              py={isMobile ? 3 : 4}
              px={isMobile ? 4 : 6}
              display="flex"
              alignItems="center"
              justifyContent="center"
              gap={2}
              textColor={isMobile ? "#2F2F34" : "#2F2F34"}
              transition="all 0.2s"
              borderRadius={isMobile ? "100px" : "10px"}
              minW={isMobile ? "auto" : "180px"}
              flex={isMobile ? 1 : "none"}
              bg={isMobile ? "white" : "transparent"}
              border={isMobile ? "1px solid #E6E9EF" : "none"}
              _hover={
                isMobile
                  ? {
                      bg: "#1819B4",
                      color: "white",
                      border: "1px solid #1819B4",
                    }
                  : {}
              }
            >
              <TabIconLabel idx={idx} label={tab.label} iconFn={tab.icon} />
            </Tab>
          ))}
        </TabList>
        <TabPanels>
          <TabPanel px={0}>
            <Box
              w={isMobile ? "100%" : "100%"}
              mx="auto"
              display="flex"
              justifyContent="center"
            >
              <FaqTab />
            </Box>
          </TabPanel>
          <TabPanel px={0}>
            <Box
              w={isMobile ? "100%" : "auto"}
              mx="auto"
              display="flex"
              justifyContent="center"
            >
              <InquiriesTab />
            </Box>
          </TabPanel>
          <TabPanel px={0}>
            <Box
              w={isMobile ? "100%" : "auto"}
              mx="auto"
              display="flex"
              justifyContent="center"
            >
              <ResourcesTab />
            </Box>
          </TabPanel>
        </TabPanels>
      </Tabs>
    </Box>
  );
};

export default CustomerCenterTabs;
